import { Button } from '@/components/UI/Button'
import { CircleLoading } from '@/components/UI/CircleLoading'
import { Dialog } from '@/components/UI/Dialog'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * OCR 読み取り失敗ダイアログの表示バリアント。
 * - retryable:  リトライ残あり → 再撮影する
 * - checking:   リトライ切れ直後、補填申請可否を GET 確認中（ローディング）
 * - applicable: 補填申請可 → 補填申請をする
 * - notApplicable: 補填申請不可 → 閉じるのみ
 */
export type OcrFailureVariant = 'retryable' | 'checking' | 'applicable' | 'notApplicable'

type Props = {
  variant: OcrFailureVariant
  /** 再撮影ボタン押下 */
  onRetry: () => void
  /** 補填申請ボタン押下 */
  onApply: () => void
  /** 閉じるボタン押下 */
  onClose: () => void
  scrollLock?: boolean
}

/**
 * OCR 読み取り失敗ダイアログ
 * スタンプ獲得のため OCR で撮影した画像が読み取れなかった場合に表示する。
 * リトライ回数（maxRetry）とバックエンドの補填申請可否（applicable）に応じて内容を出し分ける。
 */
export const DialogOcrReadFailure = memo(({ variant, onRetry, onApply, onClose, scrollLock = false }: Props) => {
  const { t } = useTranslation()

  const title =
    variant === 'notApplicable'
      ? t('spot_detail.ocr.failure.stamp_failed_title', 'スタンプの獲得に失敗しました。')
      : t('spot_detail.ocr.failure.title', 'レシート読み取り失敗')

  return (
    <Dialog
      selectorState={true}
      compact
      showCloseButton={true}
      lockScroll={scrollLock}
      closeAction={onClose}
      title={title}
    >
      {variant === 'checking' ? (
        <div className="flex flex-col items-center gap-3 py-6">
          <CircleLoading />
          <p className="text-center text-sm text-gray-700">{t('spot_detail.ocr.failure.checking', '確認中...')}</p>
        </div>
      ) : (
        <>
          <p className="text-center text-sm text-gray-700 mt-2 mb-6">
            {variant === 'retryable' &&
              t('spot_detail.ocr.failure.message_retry', 'レシートの読み取りが失敗しました。再度お試しください')}
            {variant === 'applicable' &&
              t('spot_detail.ocr.failure.message_apply', '読み取りに失敗しました。補填申請を行ってください。')}
            {variant === 'notApplicable' &&
              t('spot_detail.ocr.failure.stamp_failed_message', 'もう一度レシートの読み込みから試してください')}
          </p>
          {variant === 'applicable' ? (
            // 補填申請可: 左右配置「閉じる」（左 / グレー・system）＋「申請する」（右 / メインカラー・primary）
            <div className="flex gap-3">
              <Button
                label={t('spot_detail.ocr.failure.close', '閉じる')}
                variant="system-back"
                size="md"
                fullWidth
                onClick={onClose}
              />
              <Button
                label={t('spot_detail.ocr.failure.submit', '申請する')}
                variant="primary"
                size="md"
                fullWidth
                onClick={onApply}
              />
            </div>
          ) : (
            // retryable: 「再撮影する」のみ / notApplicable: 「閉じる」のみ（いずれもメインカラー・primary）
            <div className="flex flex-col gap-3">
              {variant === 'retryable' ? (
                <Button
                  label={t('spot_detail.ocr.failure.retry', '再撮影する')}
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={onRetry}
                />
              ) : (
                <Button
                  label={t('spot_detail.ocr.failure.close', '閉じる')}
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={onClose}
                />
              )}
            </div>
          )}
        </>
      )}
    </Dialog>
  )
})

DialogOcrReadFailure.displayName = 'DialogOcrReadFailure'
